import React, { useState, useRef } from 'react';
import { Form, Field } from 'react-final-form';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonFunctions,
  buttonTextSizes,
} from '../../shared/Button';
import InlineHelpText from '../../shared/InlineHelpText';

import {
  searchText,
  submitText,
  cancelText,
  idText,
  titleText,
} from '../../shared/SharedInternationalizations';
import HelpIcon from '../../shared/HelpIcon';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import SvgXIcon from '../../svg/XIcon';

function UserPanicButtonsForm(props) {
  const { user, onSubmit, buttons, previous } = props;

  let userButtons = [];

  if (user.userbuttons) {
    userButtons = user.userbuttons.map((b) => b.button_id);
  }
  let buttonsArr = [];
  if (buttons) {
    buttonsArr = Object.values(buttons);
  }
  const [buttonsFilter, setButtonsFilter] = useState('');

  const [
    showSelectedButtonsHelpText,
    setShowSelectedButtonsHelpText,
  ] = useState(false);
  function toggleSelectedButtonsHelpText(value) {
    setShowSelectedButtonsHelpText(value);
  }
  const selectedButtonsHelpTextRef = useRef();
  const selectedButtonsHelpText = (
    <FormattedMessage
      id="userPanicButtons.selectedButtonsHelpText"
      defaultMessage="These are buttons that are selected"
    />
  );
  const selectedButtonsText = (
    <FormattedMessage
      id="userPanicButtons.selectedButtonsText"
      defaultMessage="Selected Buttons"
    />
  );

  /**
   * Function to set the search value for active filtering for users
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setUsersFilter - Updates local state with the value typed into the search bar
   */
  const buttonsSearchFilter = (event) => {
    const value = event.target.value;

    setButtonsFilter(value);
  };

  /**
   * Function to generate the users table
   * @returns {object} - The table of users
   */
  const buttonsTable = (values) => {
    const initialFilterList = buttonsArr.filter((d) => {
      if (values.buttons.includes(d.button_id)) {
        return false;
      } else {
        return true;
      }
    });

    const buttonsArray = initialFilterList.map((d) => {
      return {
        selector: {
          sortBy: d.button_id,
          display: d.button_id,
        },
        id: {
          sortBy: d.button_id,
          display: d.button_id,
        },
        title: {
          sortBy: d.title.toUpperCase(),
          display: d.title,
        },
      };
    });

    let searchedButtons = [];
    // Take the array and filter based on the text entered in the search bar.
    if (buttonsFilter !== '') {
      searchedButtons = buttonsArray.filter((item) => {
        let filterResult = false;
        // Make the device name uppercase for case insensitivity
        const title = item.title.display.toUpperCase();
        const id = item.id.display;
        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        if (
          title.includes(buttonsFilter.toUpperCase()) ||
          buttonsFilter.includes(id)
        ) {
          filterResult = true;
        }
        return filterResult;
      });
    } else {
      searchedButtons = buttonsArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },
      {
        isSortable: true,
        sortBy: 'id',
        display: idText,
      },
      {
        isSortable: true,
        sortBy: 'title',
        display: titleText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="buttons"
        selectorParamValue="buttons"
        sortParam="id"
        tableData={searchedButtons}
        selectorType={selectorTypes.checkbox}
        tableStyles="table__constrained table-layout__2-column--selector"
        data-test="buttons-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const selectedButtonsTable = (values) => {
    let table;
    let selectedButtons = values.buttons;

    let buttonsRows;
    if (selectedButtons.length > 0) {
      if (selectedButtons) {
        const filteredButtons = [];
        selectedButtons.map((id) => {
          let thisId = id;
          if (typeof id === 'string') {
            thisId = parseInt(thisId);
          }
          if (buttons[thisId]) {
            filteredButtons.push(buttons[thisId]);
          }
          return '';
        });
        buttonsRows = filteredButtons.map((d) => {
          const removeButton = (
            <label>
              <Field
                className="visually-hidden"
                name="buttons"
                component="input"
                type="checkbox"
                value={d.button_id}
              />
              <SvgXIcon />
            </label>
          );

          return {
            selector: {
              sortBy: '',
              display: removeButton,
            },
            id: {
              sortBy: d.button_id,
              display: d.button_id,
            },
            title: {
              sortBy: d.title.toUpperCase(),
              display: d.title,
            },
          };
        });
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: true,
          sortBy: 'id',
          display: idText,
        },
        {
          isSortable: true,
          sortBy: 'title',
          display: titleText,
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="id"
          tableData={buttonsRows}
          tableStyles="table__constrained table-layout__2-column--selector"
          data-test="selected-buttons-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  return (
    <div className="content-display__container">
      <Form
        data-test="panic-buttons-form"
        onSubmit={onSubmit}
        keepDirtyOnReinitialize={true}
        initialValues={{
          buttons: userButtons || [],
          initialButtons: userButtons || [],
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          values,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="associate-users-units__form-container">
            <Field
              className="visually-hidden"
              name="associateId"
              component="input"
              type="text"
            />
            <Field
              className="visually-hidden"
              name="initialUsers"
              component="input"
              type="select"
            />
            <div className="form__section-container form__section-container--grid">
              <div className="form__item-container">
                <div className="align-center">
                  <label
                    className="text-medium text-bold form-item__field-message--required"
                    htmlFor="usersId">
                    {selectedButtonsText}
                  </label>
                  <HelpIcon
                    onClick={() =>
                      selectedButtonsHelpTextRef.current.toggleHelpText()
                    }
                    toggled={showSelectedButtonsHelpText}
                    styles="margin-left-5"
                  />
                </div>
              </div>
            </div>
            <div className="width-100 margin-bottom-10">
              <InlineHelpText
                ref={selectedButtonsHelpTextRef}
                toggleFunction={toggleSelectedButtonsHelpText}
                message={selectedButtonsHelpText}
              />
            </div>
            <div className="form__section-container form__section-container--grid margin-bottom-10">
              {selectedButtonsTable(values)}

              <div className="table__header padding-10">
                <label className="text-medium text-bold" htmlFor="usersFilter">
                  {searchText}:
                </label>

                <input
                  type="text"
                  id="usersFilter"
                  name="usersFilter"
                  onChange={buttonsSearchFilter}
                  className="message-table__filter-search-box margin-left-10"
                  value={buttonsFilter}
                />
              </div>

              {buttonsTable(values)}
            </div>
            <div className="form__section-container form__section-container--spread associate-users-units__form-buttons-container">
              <div className="form__items-group-container" />
              <div className="form__items-group-container">
                <Link
                  className="link link-text"
                  to={previous}
                  data-test="associate-devices-form-cancel-link">
                  {cancelText}
                </Link>
                <Button
                  buttonFunction={buttonFunctions.submit}
                  text={submitText}
                  buttonType={buttonTypes.success}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15"
                  disabled={pristine || invalid}
                />
              </div>
            </div>
          </form>
        )}
      />
    </div>
  );
}

export default UserPanicButtonsForm;
